// src/contexts/NotificationContext.js - COMPLETE FIXED VERSION
// VERSION: 3.2 - FIXED: Chat notifications with proper deduplication and no hook violations

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../firebaseConfig';
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    doc,
    getDoc,
    limit
} from 'firebase/firestore';
import useAuth from '../hooks/useAuth';
import { requestFCMToken, onForegroundMessage, saveFCMTokenToUser } from '../utils/fcmNotifications';
import {
    getModulePermissions,
    getModuleConfig,
    getModuleDefaultRoute,
    NOTIFICATION_MODULES
} from '../config/notificationConfig';

// Constantes
const NOTIFICATION_EXPIRY_DAYS = 7;
const MAX_NOTIFICATIONS_PER_MODULE = 100;

// Tipos de módulos disponíveis
export const MODULE_TYPES = {
    HELPDESK: 'helpdesk',
    CHAT: 'chat',
    PROJECTS: 'projeto',
    PURCHASE_REQUEST: 'requisicao_de_compra',
    PURCHASE_ORDER_APPROVAL: 'aprovacao_de_pedido_de_compra',
    SALES_ORDER: 'pedido_de_venda',
    SALES_ORDER_APPROVAL: 'aprovacao_de_pedido_de_venda',
    DEVELOPMENT: 'desenvolvimento',
    PROJECT_STATUS: 'acompanhamento_de_projeto',
    PRODUCTION_ORDERS: 'ordens_de_producao',
    PRODUCTION_PHASE: 'fases_de_producao'
};

// Helper functions
const getModuleIcon = (moduleId) => {
    const config = getModuleConfig(moduleId);
    return config?.icon || '📌';
};

const getModuleColor = (moduleId) => {
    const config = getModuleConfig(moduleId);
    return config?.color || '#6c757d';
};

const getModuleName = (moduleId) => {
    const config = getModuleConfig(moduleId);
    return config?.name || moduleId.charAt(0).toUpperCase() + moduleId.slice(1);
};

const formatCurrency = (value) => {
    if (!value && value !== 0) return 'R$ 0,00';
    return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
};

const NotificationContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [userPermissions, setUserPermissions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const [fcmToken, setFcmToken] = useState(null);

    // Refs para controle
    const processedNotificationsRef = useRef(new Map());
    const unsubscribeRefs = useRef(new Map());
    const isMounted = useRef(true);
    const isSettingNotifications = useRef(false);
    const chatChannelUnsubscribersRef = useRef([]);

    // ============================================================
    // CHAT DEDUPLICATION REFS (MOVED TO COMPONENT LEVEL)
    // ============================================================

    // Ref for processed chat message IDs (persists across renders)
    const chatProcessedRef = useRef(new Set());
    const chatMessageTimestampsRef = useRef(new Map());
    const chatLastProcessedTimeRef = useRef(new Map());
    const chatNotificationIdsRef = useRef(new Set());

    // ============================================================
    // 1. PERMISSÕES DINÂMICAS
    // ============================================================

    useEffect(() => {
        isMounted.current = true;

        if (!currentUser) {
            setUserPermissions([]);
            setIsLoading(false);
            setIsInitialized(true);
            return;
        }

        const loadPermissions = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                const userData = userDoc.data();

                const permissions = getModulePermissions(
                    userData?.role || 'Usuário',
                    userData?.permissions || []
                );

                if (isMounted.current) {
                    setUserPermissions(permissions);

                    const processed = new Map();
                    permissions.forEach(module => {
                        const key = `erp_processed_${module}_${currentUser.email}`;
                        const saved = localStorage.getItem(key);
                        if (saved) {
                            try {
                                processed.set(module, new Set(JSON.parse(saved)));
                            } catch (e) {
                                processed.set(module, new Set());
                            }
                        } else {
                            processed.set(module, new Set());
                        }
                    });
                    processedNotificationsRef.current = processed;

                    loadSavedNotifications(permissions);

                    // ============================================================
                    // LOAD CHAT PROCESSED IDs FROM LOCALSTORAGE
                    // ============================================================
                    try {
                        const savedChat = localStorage.getItem(`chat_processed_${currentUser.email}`);
                        if (savedChat) {
                            const parsed = JSON.parse(savedChat);
                            if (parsed.timestamp && Date.now() - parsed.timestamp < 86400000) {
                                chatProcessedRef.current = new Set(parsed.ids || []);
                                console.log(`📚 Loaded ${chatProcessedRef.current.size} processed chat message IDs`);
                            } else {
                                console.log('📚 Chat processed cache expired, starting fresh');
                            }
                        }
                    } catch (e) {
                        console.warn('Could not load processed chat IDs:', e);
                    }

                    setIsLoading(false);
                    setIsInitialized(true);
                }
            } catch (error) {
                console.error('❌ Error loading permissions:', error);
                if (isMounted.current) {
                    setIsLoading(false);
                    setIsInitialized(true);
                }
            }
        };

        loadPermissions();

        return () => {
            isMounted.current = false;
        };
    }, [currentUser]);

    // ============================================================
    // 2. UPDATE CHAT NOTIFICATION IDS REF
    // ============================================================

    useEffect(() => {
        chatNotificationIdsRef.current = new Set(notifications.map(n => n.id));
    }, [notifications]);

    // ============================================================
    // 3. PERSIST CHAT PROCESSED IDs
    // ============================================================

    const persistChatProcessedIds = useCallback(() => {
        if (!currentUser) return;
        try {
            localStorage.setItem(`chat_processed_${currentUser.email}`, JSON.stringify({
                ids: [...chatProcessedRef.current],
                timestamp: Date.now()
            }));
        } catch (e) {
            console.warn('Could not save processed chat IDs:', e);
        }
    }, [currentUser]);

    // ============================================================
    // 4. CARREGAR NOTIFICAÇÕES SALVAS
    // ============================================================

    const loadSavedNotifications = useCallback((permissions) => {
        if (!currentUser || !permissions || !permissions.length) return;

        const allSaved = [];
        const userEmail = currentUser.email;

        permissions.forEach(module => {
            const key = `erp_notifications_${module}_${userEmail}`;
            const saved = localStorage.getItem(key);

            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    const oneWeekAgo = new Date();
                    oneWeekAgo.setDate(oneWeekAgo.getDate() - NOTIFICATION_EXPIRY_DAYS);

                    const filtered = parsed.filter(n => {
                        const notifDate = n.timestamp ? new Date(n.timestamp) : new Date();
                        return notifDate > oneWeekAgo;
                    });

                    const moduleConfig = getModuleConfig(module);
                    const enriched = filtered.map(n => ({
                        ...n,
                        module,
                        moduleIcon: moduleConfig?.icon || getModuleIcon(module),
                        moduleColor: moduleConfig?.color || getModuleColor(module),
                        moduleName: moduleConfig?.name || getModuleName(module)
                    }));

                    allSaved.push(...enriched);
                } catch (e) {
                    console.error(`Error loading notifications for ${module}:`, e);
                }
            }
        });

        allSaved.sort((a, b) => {
            const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
            const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
            return dateB - dateA;
        });

        if (isMounted.current) {
            setNotifications(allSaved);
            const unread = allSaved.filter(n => !n.read).length;
            setUnreadCount(unread);
        }
    }, [currentUser]);

    // ============================================================
    // 5. SALVAR NOTIFICAÇÕES POR MÓDULO
    // ============================================================

    const saveNotificationsForModule = useCallback((module, moduleNotifications) => {
        if (!currentUser || !module) return;

        const userEmail = currentUser.email;
        const key = `erp_notifications_${module}_${userEmail}`;

        const toStore = moduleNotifications.slice(0, MAX_NOTIFICATIONS_PER_MODULE);
        try {
            localStorage.setItem(key, JSON.stringify(toStore));
        } catch (e) {
            console.error(`Error saving notifications for ${module}:`, e);
        }
    }, [currentUser]);

    const saveProcessedForModule = useCallback((module, processedSet) => {
        if (!currentUser || !module) return;

        const userEmail = currentUser.email;
        const key = `erp_processed_${module}_${userEmail}`;
        try {
            localStorage.setItem(key, JSON.stringify([...processedSet]));
        } catch (e) {
            console.error(`Error saving processed for ${module}:`, e);
        }
    }, [currentUser]);

    // ============================================================
    // 6. FUNÇÃO PARA ADICIONAR NOTIFICAÇÃO MANUAL
    // ============================================================

    const addNotification = useCallback((notification) => {
        if (!notification || !notification.module) {
            console.warn('Module is required for notification');
            return;
        }

        const module = notification.module;
        const config = getModuleConfig(module);

        const enriched = {
            id: notification.id || `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            ...notification,
            module: module,
            moduleIcon: config?.icon || getModuleIcon(module),
            moduleColor: config?.color || getModuleColor(module),
            moduleName: config?.name || getModuleName(module),
            timestamp: notification.timestamp || new Date().toISOString(),
            read: notification.read || false,
            route: notification.route || getModuleDefaultRoute(module)
        };

        if (isMounted.current) {
            setNotifications(prev => {
                const exists = prev.some(n => n.id === enriched.id);
                if (exists) return prev;
                return [enriched, ...prev];
            });
            setUnreadCount(prev => prev + 1);
        }

        const userEmail = currentUser?.email;
        if (userEmail) {
            const key = `erp_notifications_${module}_${userEmail}`;
            const saved = localStorage.getItem(key);
            let existing = [];
            if (saved) {
                try {
                    existing = JSON.parse(saved);
                } catch (e) {
                    existing = [];
                }
            }
            existing = [enriched, ...existing].slice(0, MAX_NOTIFICATIONS_PER_MODULE);
            try {
                localStorage.setItem(key, JSON.stringify(existing));
            } catch (e) {
                console.error(`Error saving manual notification for ${module}:`, e);
            }
        }
    }, [currentUser]);

    // ============================================================
    // 7. MÓDULO: HELPDESK - LISTENER
    // ============================================================

    const setupHelpdeskListener = useCallback(() => {
        if (!currentUser || !userPermissions.includes('helpdesk')) {
            return null;
        }

        const module = 'helpdesk';
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - NOTIFICATION_EXPIRY_DAYS);

        const ticketsQuery = query(
            collection(db, 'help-desk-tickets'),
            where('createdAt', '>=', oneWeekAgo),
            orderBy('createdAt', 'desc'),
            limit(100)
        );

        return onSnapshot(ticketsQuery, (snapshot) => {
            if (!isMounted.current) return;

            const newNotifications = [];
            const processed = processedNotificationsRef.current.get(module) || new Set();
            const currentNotifications = notifications.filter(n => n.module === module);
            const existingIds = new Set(currentNotifications.map(n => n.id));

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const ticketData = change.doc.data();
                    const ticketId = change.doc.id;

                    if (ticketData.status === 'Fechado') return;
                    if (processed.has(ticketId)) return;
                    if (existingIds.has(ticketId)) return;
                    if (!ticketData.createdAt) return;

                    const config = getModuleConfig(module);
                    const notification = {
                        id: ticketId,
                        ticketId: ticketId,
                        type: 'new_ticket',
                        module: module,
                        moduleIcon: config?.icon || getModuleIcon(module),
                        moduleColor: config?.color || getModuleColor(module),
                        moduleName: config?.name || getModuleName(module),
                        title: 'Novo Ticket de Suporte',
                        message: `${ticketData.userName || ticketData.userEmail} abriu: "${ticketData.assunto?.substring(0, 60) || ''}${ticketData.assunto?.length > 60 ? '...' : ''}"`,
                        fullMessage: ticketData.descricao || '',
                        assunto: ticketData.assunto || '',
                        userName: ticketData.userName || ticketData.userEmail,
                        userEmail: ticketData.userEmail || '',
                        status: ticketData.status || 'Aberto',
                        createdAt: ticketData.createdAt,
                        timestamp: new Date().toISOString(),
                        read: false,
                        priority: ticketData.prioridade || 'Média',
                        route: `/helpdesk/gerenciamento-de-tickets?ticketId=${ticketId}`,
                        data: { ticketId }
                    };

                    newNotifications.push(notification);
                    processed.add(ticketId);
                }
            });

            if (newNotifications.length > 0 && isMounted.current) {
                setNotifications(prev => {
                    const currentIds = new Set(prev.map(n => n.id));
                    const uniqueNew = newNotifications.filter(n => !currentIds.has(n.id));
                    return [...uniqueNew, ...prev];
                });
                setUnreadCount(prev => prev + newNotifications.length);

                saveProcessedForModule(module, processed);
                processedNotificationsRef.current.set(module, processed);
            }
        }, (error) => {
            console.error(`❌ Error listening to ${module}:`, error);
        });
    }, [currentUser, userPermissions, notifications, saveProcessedForModule]);

    // ============================================================
    // 8. MÓDULO: CHAT - LISTENER (FIXED - NO HOOKS INSIDE)
    // ============================================================

    const setupChatListener = useCallback(() => {
        if (!currentUser || !userPermissions.includes('chat')) {
            return null;
        }

        const module = 'chat';
        const userId = currentUser.uid;

        console.log(`📨 Setting up chat listener for user ${userId}`);

        // ============================================================
        // LOAD PROCESSED IDs FROM REF (already loaded in useEffect)
        // ============================================================

        // Use the refs defined at component level
        const processedRef = chatProcessedRef;
        const messageTimestamps = chatMessageTimestampsRef;
        const lastProcessedTime = chatLastProcessedTimeRef;
        const notificationIdsRef = chatNotificationIdsRef;

        // ============================================================
        // CHANNEL LISTENER
        // ============================================================

        const channelsQuery = query(
            collection(db, 'chat_channels'),
            where('members', 'array-contains', userId)
        );

        const unsubscribers = [];

        const unsubChannels = onSnapshot(channelsQuery, (channelsSnapshot) => {
            // Clean up old message listeners
            unsubscribers.forEach(unsub => {
                try { unsub(); } catch (e) { /* ignore */ }
            });
            unsubscribers.length = 0;

            channelsSnapshot.forEach((channelDoc) => {
                const channelId = channelDoc.id;
                const channelData = channelDoc.data();

                // ============================================================
                // CRITICAL FIX: Query ONLY messages from the last 10 seconds
                // ============================================================

                const recentTime = new Date();
                recentTime.setSeconds(recentTime.getSeconds() - 10);

                const messagesQuery = query(
                    collection(db, 'chat_channels', channelId, 'messages'),
                    where('timestamp', '>=', recentTime),
                    orderBy('timestamp', 'desc'),
                    limit(5)
                );

                const unsubMsg = onSnapshot(messagesQuery, (msgSnapshot) => {
                    const now = new Date();
                    const newNotifications = [];

                    // Track which messages we're processing in this batch
                    const batchProcessedIds = new Set();

                    // Process only 'added' changes
                    const addedChanges = msgSnapshot.docChanges().filter(c => c.type === 'added');

                    if (addedChanges.length === 0) return;

                    console.log(`📨 Processing ${addedChanges.length} added messages in channel ${channelId}`);

                    addedChanges.forEach((change) => {
                        const msgData = change.doc.data();
                        const msgId = change.doc.id;

                        // ============================================================
                        // STRICT DEDUPLICATION CHECKS - ALL MUST PASS
                        // ============================================================

                        // Check 1: Not sent by current user
                        if (msgData.senderId === userId) {
                            console.log(`⏭️ [${msgId}] Own message, skipping`);
                            return;
                        }

                        // Check 2: Must have timestamp
                        if (!msgData.timestamp) {
                            console.log(`⏭️ [${msgId}] No timestamp, skipping`);
                            return;
                        }

                        // Check 3: Convert timestamp to Date
                        let msgTimestamp;
                        try {
                            msgTimestamp = msgData.timestamp.toDate?.() || new Date(msgData.timestamp);
                        } catch (e) {
                            console.log(`⏭️ [${msgId}] Invalid timestamp, skipping`);
                            return;
                        }

                        // Check 4: Message must be VERY recent (less than 5 seconds old)
                        const secondsAgo = (now - msgTimestamp) / 1000;
                        if (secondsAgo > 5) {
                            console.log(`⏭️ [${msgId}] Old message (${secondsAgo.toFixed(1)}s old), skipping`);
                            return;
                        }

                        // Check 5: Check if already processed in this session
                        if (processedRef.current.has(msgId)) {
                            console.log(`⏭️ [${msgId}] Already processed in session, skipping`);
                            return;
                        }

                        // Check 6: Check if already in notification state
                        if (notificationIdsRef.current.has(msgId)) {
                            console.log(`⏭️ [${msgId}] Already in notification state, skipping`);
                            processedRef.current.add(msgId);
                            return;
                        }

                        // Check 7: Check if processed in this batch
                        if (batchProcessedIds.has(msgId)) {
                            console.log(`⏭️ [${msgId}] Already processed in this batch, skipping`);
                            return;
                        }

                        // Check 8: Rate limiting - prevent rapid-fire duplicates
                        if (messageTimestamps.current.has(msgId)) {
                            const firstSeen = messageTimestamps.current.get(msgId);
                            if (now - firstSeen < 5000) {
                                console.log(`⏭️ [${msgId}] Rate limited (${now - firstSeen}ms since first seen), skipping`);
                                return;
                            }
                        }

                        // Check 9: Channel cooldown - prevent processing too many from same channel
                        const channelKey = `channel_${channelId}`;
                        const lastChannelProcess = lastProcessedTime.current.get(channelKey) || 0;
                        if (now - lastChannelProcess < 2000) {
                            console.log(`⏭️ [${msgId}] Channel cooldown, skipping`);
                            return;
                        }
                        lastProcessedTime.current.set(channelKey, now);

                        // ============================================================
                        // ALL CHECKS PASSED - THIS IS A GENUINE NEW MESSAGE
                        // ============================================================

                        console.log(`✅ [${msgId}] New message detected! (${secondsAgo.toFixed(1)}s old)`);

                        // Mark as processed
                        processedRef.current.add(msgId);
                        batchProcessedIds.add(msgId);
                        messageTimestamps.current.set(msgId, now);

                        // Build notification
                        const config = getModuleConfig(module);
                        const senderName = msgData.senderName || 'Alguém';
                        const messagePreview = (msgData.text || '').substring(0, 60);
                        const channelDisplayName = channelData.isPrivate
                            ? senderName
                            : channelData.name || 'Grupo';

                        const notification = {
                            id: msgId,
                            type: 'new_message',
                            module: module,
                            moduleIcon: config?.icon || getModuleIcon(module),
                            moduleColor: config?.color || getModuleColor(module),
                            moduleName: config?.name || getModuleName(module),
                            title: `💬 ${senderName}`,
                            message: `${messagePreview}${(msgData.text || '').length > 60 ? '...' : ''}`,
                            fullMessage: msgData.text || '',
                            senderName: senderName,
                            senderId: msgData.senderId,
                            channelId: channelId,
                            channelName: channelDisplayName,
                            timestamp: msgTimestamp.toISOString(),
                            read: false,
                            actionType: 'open_chat',
                            route: `/chat?channel=${channelId}`,
                            data: {
                                messageId: msgId,
                                channelId: channelId,
                                senderId: msgData.senderId,
                                senderName: senderName
                            },
                            isChatNotification: true,
                            needsChannelId: true
                        };

                        newNotifications.push(notification);
                    });

                    // ============================================================
                    // BATCH UPDATE STATE
                    // ============================================================

                    if (newNotifications.length > 0 && isMounted.current) {
                        console.log(`📨 Adding ${newNotifications.length} new chat notifications`);

                        // Update notifications state
                        setNotifications(prev => {
                            const existingIds = new Set(prev.map(n => n.id));
                            const uniqueNew = newNotifications.filter(n => !existingIds.has(n.id));
                            if (uniqueNew.length === 0) return prev;
                            return [...uniqueNew, ...prev];
                        });

                        // Update unread count
                        setUnreadCount(prev => prev + newNotifications.length);

                        // Persist processed IDs
                        persistChatProcessedIds();

                        // Update notification IDs ref
                        newNotifications.forEach(n => {
                            notificationIdsRef.current.add(n.id);
                        });

                        // Send browser notifications
                        newNotifications.forEach(n => {
                            // Only send browser notification if app is in background
                            if (document.visibilityState !== 'visible') {
                                import('../utils/browserNotifications')
                                    .then(({ sendChatNotification }) => {
                                        sendChatNotification(
                                            n.senderName,
                                            n.fullMessage,
                                            n.channelId,
                                            n.channelName
                                        );
                                    })
                                    .catch(() => {
                                        console.log('Push notification not available');
                                    });
                            }
                        });
                    }

                    // ============================================================
                    // CLEANUP OLD PROCESSED ENTRIES (keep last 1000)
                    // ============================================================

                    if (processedRef.current.size > 1000) {
                        const toDelete = Math.min(processedRef.current.size - 1000, 100);
                        const iterator = processedRef.current.values();
                        for (let i = 0; i < toDelete; i++) {
                            const id = iterator.next().value;
                            if (id) processedRef.current.delete(id);
                        }
                        persistChatProcessedIds();
                    }

                }, (error) => {
                    console.error(`❌ Error listening to messages in channel ${channelId}:`, error);
                });

                unsubscribers.push(unsubMsg);
            });
        }, (error) => {
            console.error(`❌ Error listening to chat channels:`, error);
        });

        // ============================================================
        // CLEANUP
        // ============================================================

        return () => {
            console.log('🧹 Cleaning up chat listeners');
            try { unsubChannels(); } catch (e) { /* ignore */ }
            unsubscribers.forEach(unsub => {
                try { unsub(); } catch (e) { /* ignore */ }
            });
            unsubscribers.length = 0;

            // Persist before cleanup
            persistChatProcessedIds();
        };
    }, [currentUser, userPermissions, persistChatProcessedIds]);

    // ============================================================
    // 9. MÓDULO: PURCHASE REQUEST - LISTENER
    // ============================================================

    const setupPurchaseRequestListener = useCallback(() => {
        if (!currentUser || !userPermissions.includes('requisicao_de_compra')) {
            return null;
        }

        const module = 'requisicao_de_compra';
        const processed = processedNotificationsRef.current.get(module) || new Set();

        const notificationsQuery = query(
            collection(db, 'notifications'),
            where('userId', '==', currentUser.uid),
            where('module', '==', 'requisicao_de_compra'),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        return onSnapshot(notificationsQuery, (snapshot) => {
            if (!isMounted.current) return;

            const newNotifications = [];

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    const notifId = change.doc.id;

                    if (processed.has(notifId)) return;

                    const config = getModuleConfig(module);
                    const notification = {
                        id: notifId,
                        ...data,
                        module: module,
                        moduleIcon: config?.icon || getModuleIcon(module),
                        moduleColor: config?.color || getModuleColor(module),
                        moduleName: config?.name || getModuleName(module),
                        read: data.read || false,
                        timestamp: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
                    };

                    newNotifications.push(notification);
                    processed.add(notifId);
                }
            });

            if (newNotifications.length > 0 && isMounted.current) {
                setNotifications(prev => {
                    const currentIds = new Set(prev.map(n => n.id));
                    const uniqueNew = newNotifications.filter(n => !currentIds.has(n.id));
                    return [...uniqueNew, ...prev];
                });
                setUnreadCount(prev => prev + newNotifications.length);

                saveProcessedForModule(module, processed);
                processedNotificationsRef.current.set(module, processed);
            }
        }, (error) => {
            console.error(`❌ Error listening to purchase requests:`, error);
        });
    }, [currentUser, userPermissions, notifications, saveProcessedForModule]);

    // ============================================================
    // 10. MÓDULO: PURCHASE ORDER APPROVAL - LISTENER
    // ============================================================

    const setupPurchaseOrderApprovalListener = useCallback(() => {
        if (!currentUser || !userPermissions.includes('aprovacao_de_pedido_de_compra')) {
            return null;
        }

        const module = 'aprovacao_de_pedido_de_compra';
        const processed = processedNotificationsRef.current.get(module) || new Set();

        const ordersQuery = query(
            collection(db, 'purchase-orders'),
            where('status', '==', 'pending'),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        return onSnapshot(ordersQuery, (snapshot) => {
            if (!isMounted.current) return;

            const newNotifications = [];
            const currentNotifications = notifications.filter(n => n.module === module);
            const existingIds = new Set(currentNotifications.map(n => n.id));

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const orderData = change.doc.data();
                    const orderId = change.doc.id;

                    const notificationKey = `po_approval_${orderId}`;
                    if (processed.has(notificationKey)) return;
                    if (existingIds.has(notificationKey)) return;

                    if (orderData.status !== 'pending') return;

                    const config = getModuleConfig(module);
                    const totalValue = orderData.calculatedValues?.total || 0;

                    const notification = {
                        id: notificationKey,
                        type: 'purchase_order_pending_approval',
                        module: module,
                        moduleIcon: config?.icon || getModuleIcon(module),
                        moduleColor: config?.color || getModuleColor(module),
                        moduleName: config?.name || getModuleName(module),
                        title: 'Novo Pedido Aguardando Aprovação',
                        message: `Pedido ${orderData.code} - ${orderData.supplierName || 'Fornecedor'} - ${formatCurrency(totalValue)}`,
                        fullMessage: `Detalhes do pedido:\n• Código: ${orderData.code}\n• Fornecedor: ${orderData.supplierName || 'N/A'}\n• Valor Total: ${formatCurrency(totalValue)}\n• Itens: ${orderData.items?.length || 0}\n• Solicitante: ${orderData.responsible || 'N/A'}`,
                        orderCode: orderData.code,
                        supplierName: orderData.supplierName || 'Fornecedor',
                        totalValue: totalValue,
                        timestamp: orderData.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
                        read: false,
                        priority: 'Alta',
                        route: `/pedidos/aprovacao-de-pedido-de-compra`,
                        data: {
                            orderId: orderId,
                            orderCode: orderData.code,
                            supplierName: orderData.supplierName,
                            totalValue: totalValue
                        }
                    };

                    newNotifications.push(notification);
                    processed.add(notificationKey);
                }
            });

            if (newNotifications.length > 0 && isMounted.current) {
                setNotifications(prev => {
                    const currentIds = new Set(prev.map(n => n.id));
                    const uniqueNew = newNotifications.filter(n => !currentIds.has(n.id));
                    return [...uniqueNew, ...prev];
                });
                setUnreadCount(prev => prev + newNotifications.length);

                saveProcessedForModule(module, processed);
                processedNotificationsRef.current.set(module, processed);
            }
        }, (error) => {
            console.error(`❌ Error listening to purchase order approvals:`, error);
        });
    }, [currentUser, userPermissions, notifications, saveProcessedForModule]);

    // ============================================================
    // 11. MÓDULO: SALES ORDER - LISTENER
    // ============================================================

    const setupSalesOrderListener = useCallback(() => {
        if (!currentUser || !userPermissions.includes('pedido_de_venda')) {
            return null;
        }

        const module = 'pedido_de_venda';
        const processed = processedNotificationsRef.current.get(module) || new Set();

        const ordersQuery = query(
            collection(db, 'sales-orders'),
            where('status', 'in', ['pending', 'approved']),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        return onSnapshot(ordersQuery, (snapshot) => {
            if (!isMounted.current) return;

            const newNotifications = [];
            const currentNotifications = notifications.filter(n => n.module === module);
            const existingIds = new Set(currentNotifications.map(n => n.id));

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const orderData = change.doc.data();
                    const orderId = change.doc.id;

                    const notificationKey = `so_${orderId}`;
                    if (processed.has(notificationKey)) return;
                    if (existingIds.has(notificationKey)) return;

                    if (orderData.status === 'draft') return;

                    const config = getModuleConfig(module);
                    const totalValue = orderData.calculatedValues?.total || 0;
                    const isPending = orderData.status === 'pending';
                    const isApproved = orderData.status === 'approved';

                    let title, message, priority, type;
                    if (isPending) {
                        title = 'Novo Pedido Aguardando Aprovação';
                        message = `Pedido ${orderData.code} - ${orderData.clientName || 'Cliente'} - ${formatCurrency(totalValue)}`;
                        priority = 'Alta';
                        type = 'sales_order_pending_approval';
                    } else if (isApproved) {
                        title = 'Pedido Aprovado';
                        message = `Pedido ${orderData.code} - ${orderData.clientName || 'Cliente'} foi aprovado`;
                        priority = 'Média';
                        type = 'sales_order_approved';
                    } else {
                        return;
                    }

                    const notification = {
                        id: notificationKey,
                        type: type,
                        module: module,
                        moduleIcon: config?.icon || getModuleIcon(module),
                        moduleColor: config?.color || getModuleColor(module),
                        moduleName: config?.name || getModuleName(module),
                        title: title,
                        message: message,
                        fullMessage: `Detalhes do pedido:\n• Código: ${orderData.code}\n• Cliente: ${orderData.clientName || 'N/A'}\n• Valor Total: ${formatCurrency(totalValue)}\n• Itens: ${orderData.items?.length || 0}\n• Responsável: ${orderData.responsible || 'N/A'}`,
                        orderCode: orderData.code,
                        clientName: orderData.clientName || 'Cliente',
                        totalValue: totalValue,
                        timestamp: orderData.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
                        read: false,
                        priority: priority,
                        route: `/pedidos/pedido-de-venda`,
                        data: {
                            orderId: orderId,
                            orderCode: orderData.code,
                            clientName: orderData.clientName,
                            totalValue: totalValue,
                            status: orderData.status
                        }
                    };

                    newNotifications.push(notification);
                    processed.add(notificationKey);
                }
            });

            if (newNotifications.length > 0 && isMounted.current) {
                setNotifications(prev => {
                    const currentIds = new Set(prev.map(n => n.id));
                    const uniqueNew = newNotifications.filter(n => !currentIds.has(n.id));
                    return [...uniqueNew, ...prev];
                });
                setUnreadCount(prev => prev + newNotifications.length);

                saveProcessedForModule(module, processed);
                processedNotificationsRef.current.set(module, processed);
            }
        }, (error) => {
            console.error(`❌ Error listening to sales orders:`, error);
        });
    }, [currentUser, userPermissions, notifications, saveProcessedForModule]);

    // ============================================================
    // 12. MÓDULO: SALES ORDER APPROVAL - LISTENER
    // ============================================================

    const setupSalesOrderApprovalListener = useCallback(() => {
        if (!currentUser || !userPermissions.includes('aprovacao_de_pedido_de_venda')) {
            return null;
        }

        const module = 'aprovacao_de_pedido_de_venda';
        const processed = processedNotificationsRef.current.get(module) || new Set();

        const notificationsQuery = query(
            collection(db, 'notifications'),
            where('userId', '==', currentUser.uid),
            where('module', '==', 'aprovacao_de_pedido_de_venda'),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        return onSnapshot(notificationsQuery, (snapshot) => {
            if (!isMounted.current) return;

            const newNotifications = [];

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    const notifId = change.doc.id;

                    if (processed.has(notifId)) return;

                    const config = getModuleConfig(module);
                    const notification = {
                        id: notifId,
                        ...data,
                        module: module,
                        moduleIcon: config?.icon || getModuleIcon(module),
                        moduleColor: config?.color || getModuleColor(module),
                        moduleName: config?.name || getModuleName(module),
                        read: data.read || false,
                        timestamp: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
                    };

                    newNotifications.push(notification);
                    processed.add(notifId);
                }
            });

            if (newNotifications.length > 0 && isMounted.current) {
                setNotifications(prev => {
                    const currentIds = new Set(prev.map(n => n.id));
                    const uniqueNew = newNotifications.filter(n => !currentIds.has(n.id));
                    return [...uniqueNew, ...prev];
                });
                setUnreadCount(prev => prev + newNotifications.length);

                saveProcessedForModule(module, processed);
                processedNotificationsRef.current.set(module, processed);
            }
        }, (error) => {
            console.error(`❌ Error listening to sales order approvals:`, error);
        });
    }, [currentUser, userPermissions, notifications, saveProcessedForModule]);

    // ============================================================
    // 13. MÓDULO: DEVELOPMENT - LISTENER
    // ============================================================

    const setupDevelopmentListener = useCallback(() => {
        if (!currentUser || !userPermissions.includes('desenvolvimento')) {
            return null;
        }

        const module = 'desenvolvimento';
        const processed = processedNotificationsRef.current.get(module) || new Set();

        const notificationsQuery = query(
            collection(db, 'notifications'),
            where('userId', '==', currentUser.uid),
            where('module', '==', 'desenvolvimento'),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        return onSnapshot(notificationsQuery, (snapshot) => {
            if (!isMounted.current) return;

            const newNotifications = [];

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    const notifId = change.doc.id;

                    if (processed.has(notifId)) return;

                    const config = getModuleConfig(module);
                    const notification = {
                        id: notifId,
                        ...data,
                        module: module,
                        moduleIcon: config?.icon || getModuleIcon(module),
                        moduleColor: config?.color || getModuleColor(module),
                        moduleName: config?.name || getModuleName(module),
                        read: data.read || false,
                        timestamp: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
                    };

                    newNotifications.push(notification);
                    processed.add(notifId);
                }
            });

            if (newNotifications.length > 0 && isMounted.current) {
                setNotifications(prev => {
                    const currentIds = new Set(prev.map(n => n.id));
                    const uniqueNew = newNotifications.filter(n => !currentIds.has(n.id));
                    return [...uniqueNew, ...prev];
                });
                setUnreadCount(prev => prev + newNotifications.length);

                saveProcessedForModule(module, processed);
                processedNotificationsRef.current.set(module, processed);
            }
        }, (error) => {
            console.error(`❌ Error listening to development notifications:`, error);
        });
    }, [currentUser, userPermissions, notifications, saveProcessedForModule]);

    // ============================================================
    // 14. MÓDULO: PROJECT STATUS - LISTENER
    // ============================================================

    const setupProjectStatusListener = useCallback(() => {
        if (!currentUser || !userPermissions.includes('acompanhamento_de_projeto')) {
            return null;
        }

        const module = 'acompanhamento_de_projeto';
        const processed = processedNotificationsRef.current.get(module) || new Set();

        const notificationsQuery = query(
            collection(db, 'notifications'),
            where('userId', '==', currentUser.uid),
            where('module', '==', 'acompanhamento_de_projeto'),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        return onSnapshot(notificationsQuery, (snapshot) => {
            if (!isMounted.current) return;

            const newNotifications = [];

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    const notifId = change.doc.id;

                    if (processed.has(notifId)) return;

                    const config = getModuleConfig(module);
                    const notification = {
                        id: notifId,
                        ...data,
                        module: module,
                        moduleIcon: config?.icon || getModuleIcon(module),
                        moduleColor: config?.color || getModuleColor(module),
                        moduleName: config?.name || getModuleName(module),
                        read: data.read || false,
                        timestamp: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
                    };

                    newNotifications.push(notification);
                    processed.add(notifId);
                }
            });

            if (newNotifications.length > 0 && isMounted.current) {
                setNotifications(prev => {
                    const currentIds = new Set(prev.map(n => n.id));
                    const uniqueNew = newNotifications.filter(n => !currentIds.has(n.id));
                    return [...uniqueNew, ...prev];
                });
                setUnreadCount(prev => prev + newNotifications.length);

                saveProcessedForModule(module, processed);
                processedNotificationsRef.current.set(module, processed);
            }
        }, (error) => {
            console.error(`❌ Error listening to project status notifications:`, error);
        });
    }, [currentUser, userPermissions, notifications, saveProcessedForModule]);

    // ============================================================
    // 15. MÓDULO: PRODUCTION ORDER - LISTENER
    // ============================================================

    const setupProductionOrderListener = useCallback(() => {
        if (!currentUser || !userPermissions.includes('ordens_de_producao')) {
            return null;
        }

        const module = 'ordens_de_producao';
        const processed = processedNotificationsRef.current.get(module) || new Set();

        const notificationsQuery = query(
            collection(db, 'notifications'),
            where('userId', '==', currentUser.uid),
            where('module', '==', 'ordens_de_producao'),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        return onSnapshot(notificationsQuery, (snapshot) => {
            if (!isMounted.current) return;

            const newNotifications = [];

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    const notifId = change.doc.id;

                    if (processed.has(notifId)) return;

                    const config = getModuleConfig(module);
                    const notification = {
                        id: notifId,
                        ...data,
                        module: module,
                        moduleIcon: config?.icon || getModuleIcon(module),
                        moduleColor: config?.color || getModuleColor(module),
                        moduleName: config?.name || getModuleName(module),
                        read: data.read || false,
                        timestamp: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
                    };

                    newNotifications.push(notification);
                    processed.add(notifId);
                }
            });

            if (newNotifications.length > 0 && isMounted.current) {
                setNotifications(prev => {
                    const currentIds = new Set(prev.map(n => n.id));
                    const uniqueNew = newNotifications.filter(n => !currentIds.has(n.id));
                    return [...uniqueNew, ...prev];
                });
                setUnreadCount(prev => prev + newNotifications.length);

                saveProcessedForModule(module, processed);
                processedNotificationsRef.current.set(module, processed);
            }
        }, (error) => {
            console.error(`❌ Error listening to production order notifications:`, error);
        });
    }, [currentUser, userPermissions, notifications, saveProcessedForModule]);

    // ============================================================
    // 16. MÓDULO: PRODUCTION PHASE - LISTENER
    // ============================================================

    const setupProductionPhaseListener = useCallback(() => {
        if (!currentUser || !userPermissions.includes('fases_de_producao')) {
            return null;
        }

        const module = 'fases_de_producao';
        const processed = processedNotificationsRef.current.get(module) || new Set();

        const notificationsQuery = query(
            collection(db, 'notifications'),
            where('userId', '==', currentUser.uid),
            where('module', '==', 'fases_de_producao'),
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        return onSnapshot(notificationsQuery, (snapshot) => {
            if (!isMounted.current) return;

            const newNotifications = [];

            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added') {
                    const data = change.doc.data();
                    const notifId = change.doc.id;

                    if (processed.has(notifId)) return;

                    const config = getModuleConfig(module);
                    const notification = {
                        id: notifId,
                        ...data,
                        module: module,
                        moduleIcon: config?.icon || getModuleIcon(module),
                        moduleColor: config?.color || getModuleColor(module),
                        moduleName: config?.name || getModuleName(module),
                        read: data.read || false,
                        timestamp: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
                    };

                    newNotifications.push(notification);
                    processed.add(notifId);
                }
            });

            if (newNotifications.length > 0 && isMounted.current) {
                setNotifications(prev => {
                    const currentIds = new Set(prev.map(n => n.id));
                    const uniqueNew = newNotifications.filter(n => !currentIds.has(n.id));
                    return [...uniqueNew, ...prev];
                });
                setUnreadCount(prev => prev + newNotifications.length);

                saveProcessedForModule(module, processed);
                processedNotificationsRef.current.set(module, processed);
            }
        }, (error) => {
            console.error(`❌ Error listening to production phase notifications:`, error);
        });
    }, [currentUser, userPermissions, notifications, saveProcessedForModule]);

    // ============================================================
    // 17. SETUP DOS LISTENERS
    // ============================================================

    useEffect(() => {
        if (!currentUser || isLoading || !isInitialized) {
            return;
        }

        unsubscribeRefs.current.forEach(unsub => {
            if (typeof unsub === 'function') unsub();
        });
        unsubscribeRefs.current.clear();

        if (userPermissions.length === 0) {
            return;
        }

        const listenerMap = {
            'helpdesk': setupHelpdeskListener,
            'chat': setupChatListener,
            'requisicao_de_compra': setupPurchaseRequestListener,
            'aprovacao_de_pedido_de_compra': setupPurchaseOrderApprovalListener,
            'pedido_de_venda': setupSalesOrderListener,
            'aprovacao_de_pedido_de_venda': setupSalesOrderApprovalListener,
            'desenvolvimento': setupDevelopmentListener,
            'acompanhamento_de_projeto': setupProjectStatusListener,
            'ordens_de_producao': setupProductionOrderListener,
            'fases_de_producao': setupProductionPhaseListener
        };

        userPermissions.forEach(module => {
            if (listenerMap[module]) {
                const unsub = listenerMap[module]();
                if (unsub) {
                    unsubscribeRefs.current.set(module, unsub);
                }
            }
        });

        return () => {
            unsubscribeRefs.current.forEach(unsub => {
                if (typeof unsub === 'function') unsub();
            });
            unsubscribeRefs.current.clear();
        };
    }, [currentUser?.uid, userPermissions.join(','), isLoading, isInitialized]);

    // ============================================================
    // 18. INICIALIZAÇÃO FCM
    // ============================================================

    useEffect(() => {
        if (!isLoading && currentUser && !isInitialized) {
            const initializeFCM = async () => {
                try {
                    const token = await requestFCMToken(currentUser.uid);
                    if (token) {
                        setFcmToken(token);
                        await saveFCMTokenToUser(currentUser.uid, token, db);
                    }

                    const unsub = onForegroundMessage((payload) => {
                        if (!isMounted.current) return;

                        console.log('📩 Foreground FCM message:', payload);

                        const notificationData = payload.notification || {};
                        const data = payload.data || {};
                        const module = data.module || 'helpdesk';
                        const config = getModuleConfig(module);

                        const newNotification = {
                            id: payload.messageId || `fcm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                            type: data.type || 'notification',
                            module: module,
                            moduleIcon: config?.icon || getModuleIcon(module),
                            moduleColor: config?.color || getModuleColor(module),
                            moduleName: config?.name || getModuleName(module),
                            title: notificationData.title || 'Nova Notificação',
                            message: notificationData.body || '',
                            timestamp: new Date().toISOString(),
                            read: false,
                            route: data.route || getModuleDefaultRoute(module),
                            priority: data.priority || 'Média',
                            data: data
                        };

                        setNotifications(prev => [newNotification, ...prev]);
                        setUnreadCount(prev => prev + 1);

                        const userEmail = currentUser?.email;
                        if (userEmail) {
                            const key = `erp_notifications_${module}_${userEmail}`;
                            const saved = localStorage.getItem(key);
                            let existing = [];
                            if (saved) {
                                try {
                                    existing = JSON.parse(saved);
                                } catch (e) {
                                    existing = [];
                                }
                            }
                            existing = [newNotification, ...existing].slice(0, MAX_NOTIFICATIONS_PER_MODULE);
                            try {
                                localStorage.setItem(key, JSON.stringify(existing));
                            } catch (e) {
                                console.error(`Error saving FCM notification for ${module}:`, e);
                            }
                        }
                    });

                    if (unsub) {
                        unsubscribeRefs.current.set('fcm', unsub);
                    }
                } catch (error) {
                    console.error('❌ Error initializing FCM:', error);
                }
            };

            initializeFCM();
        }

        return () => {
            if (unsubscribeRefs.current.has('fcm')) {
                unsubscribeRefs.current.get('fcm')();
                unsubscribeRefs.current.delete('fcm');
            }
        };
    }, [isLoading, currentUser, isInitialized]);

    // ============================================================
    // 19. SALVAR NOTIFICAÇÕES PERIODICAMENTE
    // ============================================================

    useEffect(() => {
        if (!currentUser || isLoading || !isInitialized) return;
        if (notifications.length === 0) return;

        const groupedByModule = notifications.reduce((acc, n) => {
            const module = n.module || 'helpdesk';
            if (!acc[module]) acc[module] = [];
            acc[module].push(n);
            return acc;
        }, {});

        Object.entries(groupedByModule).forEach(([module, moduleNotifications]) => {
            saveNotificationsForModule(module, moduleNotifications);
        });

    }, [notifications, currentUser, isLoading, isInitialized, saveNotificationsForModule]);

    // ============================================================
    // 20. LIMPAR NOTIFICAÇÕES ANTIGAS
    // ============================================================

    useEffect(() => {
        if (!currentUser || isLoading || !isInitialized) return;
        if (notifications.length === 0) return;
        if (isSettingNotifications.current) return;

        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - NOTIFICATION_EXPIRY_DAYS);

        const filtered = notifications.filter(n => {
            const notifDate = n.timestamp ? new Date(n.timestamp) : new Date();
            return notifDate > oneWeekAgo;
        });

        if (filtered.length !== notifications.length) {
            isSettingNotifications.current = true;
            setNotifications(filtered);
            const unread = filtered.filter(n => !n.read).length;
            setUnreadCount(unread);
            setTimeout(() => {
                isSettingNotifications.current = false;
            }, 100);
        }
    }, [notifications.length, currentUser, isLoading, isInitialized]);

    // ============================================================
    // 21. FUNÇÕES DE MANIPULAÇÃO
    // ============================================================

    const markAsRead = useCallback((notificationId) => {
        if (!notificationId) return;

        setNotifications(prev => {
            const updated = prev.map(notif =>
                notif.id === notificationId ? { ...notif, read: true } : notif
            );
            const wasUnread = prev.find(n => n.id === notificationId && !n.read);
            if (wasUnread) {
                setUnreadCount(count => Math.max(0, count - 1));
            }
            return updated;
        });
    }, []);

    const markAllAsRead = useCallback(() => {
        setNotifications(prev => {
            const allRead = prev.map(notif => ({ ...notif, read: true }));
            setUnreadCount(0);
            return allRead;
        });
    }, []);

    const clearNotification = useCallback((notificationId) => {
        if (!notificationId) return;

        setNotifications(prev => {
            const filtered = prev.filter(notif => notif.id !== notificationId);
            const wasUnread = prev.find(n => n.id === notificationId && !n.read);
            if (wasUnread) {
                setUnreadCount(count => Math.max(0, count - 1));
            }
            return filtered;
        });
    }, []);

    const clearAllNotifications = useCallback(() => {
        setNotifications([]);
        setUnreadCount(0);

        if (currentUser) {
            userPermissions.forEach(module => {
                localStorage.removeItem(`erp_notifications_${module}_${currentUser.email}`);
            });
        }
    }, [currentUser, userPermissions]);

    const clearNotificationsByModule = useCallback((module) => {
        if (!module) return;

        setNotifications(prev => {
            const filtered = prev.filter(n => n.module !== module);
            const removedUnread = prev.filter(n => n.module === module && !n.read).length;
            if (removedUnread > 0) {
                setUnreadCount(count => Math.max(0, count - removedUnread));
            }
            return filtered;
        });

        if (currentUser) {
            localStorage.removeItem(`erp_notifications_${module}_${currentUser.email}`);
        }
    }, [currentUser]);

    const getNotificationsByModule = useCallback((module) => {
        if (!module) return [];
        return notifications.filter(n => n.module === module);
    }, [notifications]);

    const getUnreadByModule = useCallback((module) => {
        if (!module) return [];
        return notifications.filter(n => n.module === module && !n.read);
    }, [notifications]);

    const getUnreadCountByModule = useCallback((module) => {
        if (!module) return 0;
        return notifications.filter(n => n.module === module && !n.read).length;
    }, [notifications]);

    // ============================================================
    // 22. CONTEXT VALUE
    // ============================================================

    const value = {
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        clearNotification,
        clearAllNotifications,
        clearNotificationsByModule,
        addNotification,
        getNotificationsByModule,
        getUnreadByModule,
        getUnreadCountByModule,
        userPermissions,
        isLoading,
        fcmToken,
        isInitialized
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};